/**
 * Dexie Database for OPMODE
 * 
 * Replaces fragile localStorage with IndexedDB for:
 * - Reliable history at scale (90+ days)
 * - Automatic deduplication via unique constraints
 * - Better incremental sync with Firebase
 * - Offline-first architecture
 */

import Dexie, { Table } from 'dexie';
import { storage } from './storage';
import { isPerHandLoad } from './exerciseDatabase';
import type { OpmodeBridgeRecord, GunfighterBridgeRecord } from './tactiv-bridge';
import type { ActivityZone, WearableProvider } from './types/activity';
import type { NutritionEntry, NutritionGoals, CustomMeal } from './types/nutrition';
import { DEFAULT_NUTRITION_GOALS } from './types/nutrition';
import type { SpearheadProgress } from './types/spearhead';

// ============================================================================
// DATE HELPERS
// ============================================================================

/** Returns YYYY-MM-DD in the user's local timezone (avoids UTC date-shift bugs) */
export function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ============================================================================
// SESSION TYPES (mirroring sessionStorage.ts)
// ============================================================================

export interface DBSetLog {
  weight: number;
  weightLabel?: 'BW';
  reps: number;
  rpe: number | null;
  completed: boolean;
  /**
   * Seconds actually held on a timed set. Declared on SetLog
   * (lib/sessionStorage.ts) and emitted by the writer, but omitted here — so
   * it did not survive the Dexie round-trip and every local-first read was
   * blind to it. Same shape as the circuitSets.distance drift.
   */
  durationSec?: number;
}

export interface DBSwimSetLog {
  distance: number;
  time: string;
  rpe: number | null;
  completed: boolean;
}

export interface DBCircuitSetLog {
  round: number;
  time: string;
  rpe: number | null;
  completed: boolean;
  runTime?: string;    // MM:SS format - sprint/run time within the round (for cardio ML)
  weight?: number;     // Pounds - for weighted elements (sled, ruck, carries)
  distance?: number;   // Metres covered this round — the carry/ruck progression lever
}

export interface DBSupersetExerciseLog {
  name: string;
  label: string;
  weight: number;
  reps: number;
  rpe: number | null;
  targetLoad: number;
  completed: boolean;
}

export interface DBSupersetRoundLog {
  round: number;
  exercises: DBSupersetExerciseLog[];
  completed: boolean;
}

export interface DBIntervalSetLog {
  distance: number;
  completed: boolean;
}

export interface DBExerciseLog {
  exerciseName: string;
  exerciseType: 'weight' | 'bodyweight' | 'cardio' | 'timed' | 'circuit' | 'ruck' | 'swim-sets' | 'carry' | 'interval';
  block: string;
  format: string;
  targetSets: string;
  targetLoad: number;
  targetRPE: string;
  restPeriod: string;
  completed: boolean;
  sets?: DBSetLog[];
  swimSets?: DBSwimSetLog[];
  circuitSets?: DBCircuitSetLog[];
  supersetRounds?: DBSupersetRoundLog[];
  intervalSets?: DBIntervalSetLog[];
  actualRPE?: number;
  actualLoad?: number;
  actualReps?: number;
  actualTime?: string;
  actualDistance?: number;
  actualIncline?: number;    // Treadmill incline percentage (0-15%)
  actualRounds?: number;
  plateCarrierWeight?: number;
  notes?: string;
}

export interface DBSession {
  id: string;                     // Primary key
  oderId?: string;                 // DEPRECATED: Use userId instead (typo, kept for migration)
  userId: string;                  // User ID
  sessionType: 'pt' | 'assessment' | 'range' | 'sprint';
  programSelection?: string;
  week: number;
  day: number;
  date: Date;
  startTime: Date;
  endTime?: Date;
  duration: number;
  overallFatigue: number;
  readiness: number;
  notes?: string;
  
  // PT/Sprint sessions
  exercises?: DBExerciseLog[];
  totalVolume?: number;           // Calculated on save for quick queries
  averageRPE?: number;
  
  // Range sessions
  protocolId?: string;
  protocolName?: string;
  drills?: any[];                 // RangeDrillLog[]
  totalRounds?: number;
  overallAccuracy?: number;
  cost?: number;
  highlights?: {
    newPRs: string[];
    perfectRuns: string[];
    improvements: string[];
  };
  
  // Sync metadata
  syncedAt?: Date;
  syncStatus: 'local' | 'synced' | 'pending';
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// RECOVERY TYPES
// ============================================================================

export interface DBRecoveryEntry {
  id: string;                     // Primary key (date-based: YYYY-MM-DD)
  userId: string;
  date: Date;
  sleepHours: number;
  sleepQuality: number;           // 1-10
  soreness: number;               // 1-10
  stress: number;                 // 1-10
  energy: number;                 // 1-10
  motivation: number;             // 1-10
  readinessScore: number;         // Calculated 0-100
  notes?: string;

  // Advanced / optional inputs the app model (RecoveryMetrics) carries. These
  // were missing here, so saveRecoveryMetrics dropped them on the way into
  // Dexie: Firestore kept them but the Dexie READ (the source of truth for the
  // UI) reconstructed without them, so a user's hand-entered HRV / RHR / per-
  // muscle soreness vanished on reload — and readiness silently recomputed
  // without the RHR/HRV signal it had just been given.
  specificSoreness?: {
    shoulders?: number;
    chest?: number;
    back?: number;
    legs?: number;
    core?: number;
  };
  hrv?: number;                   // ms
  restingHR?: number;             // bpm
  bodyWeight?: number;            // lbs
  hydration?: number;             // 1-10
  nutritionAdherence?: number;    // 1-10

  // Sync metadata
  syncedAt?: Date;
  syncStatus: 'local' | 'synced' | 'pending';
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// 1RM TYPES
// ============================================================================

export interface DB1RMEntry {
  id: string;                     // Composite: `${userId}-${exerciseName}`
  oderId?: string;                 // DEPRECATED: Use userId instead
  userId: string;
  exerciseName: string;
  weight: number;
  date: Date;
  source: 'calculated' | 'tested' | 'manual';
  confidence: number;             // 0-1
  
  // Sync metadata
  syncedAt?: Date;
  syncStatus: 'local' | 'synced' | 'pending';
  updatedAt: Date;
}

// ============================================================================
// USER PROFILE (cached from Firebase)
// ============================================================================

export interface DBUserProfile {
  userId: string;                 // Primary key
  displayName?: string;
  email?: string;

  // ── Training baseline ─────────────────────────────────────────────────────
  // These are read by the load engine: smartWorkoutGenerator pulls bodyWeight
  // and experienceLevel off this record to score training level. They were
  // collected in onboarding and written ONLY to Firestore, and no sync ever
  // brought them down to Dexie — so `bodyWeightForLevel` resolved to undefined
  // for every user, and detectTrainingLevel's relative-strength branch (which
  // requires a bodyweight) never executed once in production. Everyone scored
  // 'beginner'. Persisting them here is what makes that branch real.
  bodyWeight?: number;            // lbs
  age?: number;
  gender?: 'male' | 'female';
  experienceLevel?: 'beginner' | 'intermediate' | 'advanced' | 'elite';
  programSelection?: {
    programId: string;
    programLevel?: string;        // ProgramLevel ('beginner' | 'intermediate' | 'advanced')
    startDate: string;
    currentWeek: number;
    currentDay: number;
    completedDays?: { week: number; day: number }[];
    skippedDays?: { week: number; day: number }[];
    syncVersion?: number;         // Monotonic counter for conflict resolution
    updatedAt?: string;           // ISO timestamp of last update
    restartedAt?: string;         // ISO timestamp — signals program restart across devices
  };
  stepGoal?: number;                   // Daily step goal (default 10,000)
  preferences?: Record<string, any>;
  
  // Sync metadata
  syncedAt?: Date;
  updatedAt: Date;
}

// ============================================================================
// PROGRESSION STATE (Intel/XP System)
// ============================================================================

export type Pillar = 'strength' | 'endurance' | 'bodyweight' | 'recovery' | 'consistency';

export interface DBProgressionState {
  userId: string;                 // Primary key
  version: 1;
  level: number;
  levelIntel: number;
  lifetimeIntel: number;
  weeklyIntel: number;
  weekKey: string;                // ISO week key for weekly reset
  awardedSessionIds: string[];    // Recent session IDs for idempotency
  pillarIntel: Record<Pillar, number>;
  operation: {
    id: string;
    name: string;
    startISO: string;
    endISO: string;
    intelGoal: number;
    intelEarned: number;
  };
  
  // Rank System
  rank: {
    current: string;              // e.g., "E-5"
    title: string;                // e.g., "Sergeant"
    intel: number;                // Total Intel earned (for rank progression)
    prestigeLevel: number;        // 0-10
  };
  
  // Unlocked Cosmetics
  unlockedCallingCards: string[]; // IDs of unlocked calling cards
  unlockedEmblems: string[];      // IDs of unlocked emblems
  unlockedTitles: string[];       // IDs of unlocked titles
  unlockedAvatars: string[];      // IDs of unlocked avatars
  
  // Current Loadout
  equippedCallingCard: string;    // ID of equipped calling card
  equippedEmblem: string;         // ID of equipped emblem
  equippedTitle: string;          // ID of equipped title
  equippedAvatar: string;         // ID of equipped avatar
  
  // Streak & Daily Tracking
  lastSessionDate?: string;       // ISO date of last session (for daily bonus)
  streakDays: number;             // Current streak count
  lastStreakDate?: string;        // Last date that counted toward streak
  streakFreezeUsedThisMonth: boolean; // Whether freeze was used this month
  streakFreezeMonthKey?: string;  // YYYY-MM for monthly reset
  longestStreak: number;          // Personal best streak

  // Milestone Tracking
  milestonesUnlocked: string[];              // IDs of unlocked milestones
  operatorScoreMilestonesUnlocked?: string[]; // IDs of unlocked OS milestones (40, 50, 60, etc.)

  // Archetype System
  unlockedArchetypes: string[];   // IDs of unlocked archetypes
  equippedArchetype: string | null; // ID of equipped archetype
  archetypeProgress: {
    'pack-mule': {
      ruckMiles: number;
      ruckSessions: number;
      longestRuckWith50lbs: number;
      enduranceIntel: number;
      operatorScore: number;
      unlocked: boolean;
    };
    'breacher': {
      lifetimeVolume: number;
      oneRMTotal: number;
      strengthSessions: number;
      strengthIntel: number;
      operatorScore: number;
      unlocked: boolean;
    };
    'scout': {
      runMiles: number;
      cardioSessions: number;
      fastestMile: number;          // seconds
      enduranceIntel: number;
      operatorScore: number;
      unlocked: boolean;
    };
    'assaulter': {
      totalPullUps: number;
      totalPushUps: number;
      bodyweightSessions: number;
      maxMuscleUpsInSession: number;
      bodyweightIntel: number;
      operatorScore: number;
      unlocked: boolean;
    };
    'operator': {
      minPillarIntel: number;       // Minimum across all 5 pillars
      totalSessions: number;
      programsCompleted: number;
      operatorScore: number;
      avgReadiness30d: number;
      readinessDaysAbove70: number;
      unlocked: boolean;
    };
  };

  // Weekly Mission Completions
  missionCompletions: {
    weekKey: string;              // ISO week key (resets weekly)
    completedMissionIds: string[]; // IDs of completed missions this week
    intelEarnedFromMissions: number; // Total Intel earned from missions this week
  };

  // Cross-App Integration (GunfighterOS)
  gunfighterUserId?: string;      // Linked GunfighterOS user ID
  gunfighterLinkedAt?: string;    // ISO timestamp when accounts were linked
  gunfighterLastSync?: string;    // ISO timestamp of last data sync
  linkBonusClaimed?: boolean;     // Whether the one-time link bonus was claimed
  dualTrainingWeeks?: number;     // Consecutive weeks with sessions in both apps
  unlockedCrossAppCosmetics?: string[]; // IDs of unlocked cross-app cosmetics
  unlockedCrossAppAchievements?: string[]; // IDs of unlocked cross-app achievements
  achievementDates?: Record<string, string>; // Achievement ID -> unlock date
  operatorScore?: number;         // Cached operator score for cross-app display
  sameDayCombos?: number;         // Count of same-day physical + tactical sessions
  totalSessions?: number;         // Cached total OPMODE sessions

  // Sync metadata
  syncStatus: 'local' | 'synced' | 'pending';
  syncedAt?: Date;
  updatedAt: Date;
}

// ============================================================================
// ASSESSMENT SESSION TYPES (Multi-day aggregated assessments)
// ============================================================================

export interface DBDay1Results {
  completedAt: string;
  // Weigh-in (updates user profile)
  weighIn: { weight: number; unit: 'lbs' | 'kg' };
  // Deadlift - trap bar preferred, conventional has reduced standards
  deadlift3RM: {
    weight: number;
    rpe?: number;
    equipment: 'trap-bar' | 'conventional';
  };
  benchPressMaxReps: { reps: number; rpe?: number };
  plankMaxTime: { seconds: number };
  handReleasePushups: { reps: number; timeLimit: 120 };
  run1_5Mile: { seconds: number };
}

export interface DBDay3Results {
  completedAt: string;
  tacticalAMRAP: {
    rounds: number;
    partial?: {
      pullups: number;    // 0-5 (varies by level)
      pushups: number;    // 0-10
      squats: number;     // 0-15
      runMeters: number;  // 0-200
    };
    partialReps?: string; // Legacy field, ignored for scoring
    rpe?: number;
  };
}

export interface DBDay5Results {
  completedAt: string;
  ruck6Mile: { seconds: number; loadLbs: number };
}

export interface DBAssessmentSession {
  id: string;
  odexieId: string;  // User ID
  programId: string;
  weekNumber: 1 | 6 | 7 | 13;
  startedAt: string;
  completedAt?: string;

  // Multi-day results
  day1: DBDay1Results | null;
  day3: DBDay3Results | null;
  day5: DBDay5Results | null;

  // Computed scores (calculated on completion)
  // Grade scale: developing → passing → proficient → ready → extended
  overallGrade?: 'developing' | 'passing' | 'proficient' | 'ready' | 'extended';
  overallScore?: number;
  categoryScores?: {
    strength: number;
    endurance: number;
    workCapacity: number;
  };

  // Sync metadata
  syncStatus: 'local' | 'synced' | 'pending';
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// AUTO-PROGRESS DECISIONS (Weight progression between sessions)
// ============================================================================

export interface DBAutoProgressDecision {
  id: string;                      // `${userId}_${exerciseName}`
  userId: string;
  exerciseName: string;
  currentWeight: number;
  suggestedWeight: number;
  action: 'increase' | 'decrease' | 'maintain';
  percentage: number;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  dataPoints: number;
  updatedAt: Date;
}

// ============================================================================
// INTERVAL PROGRESSION DECISIONS (HIIT/Sprint training)
// ============================================================================

export type DBIntervalFormat = '30/60' | '30/45' | '30/30' | '60/120' | '60/90' | '60/60' | 'custom';
export type DBIntervalProgressionPath =
  | 'add_intervals'
  | 'reduce_rest'
  | 'increase_work'
  | 'upgrade_format'
  | 'maintain'
  | 'deload';

export interface DBIntervalProgressDecision {
  id: string;                      // `${userId}_interval_${exerciseName}`
  userId: string;
  exerciseName: string;
  currentFormat: DBIntervalFormat;
  currentIntervals: number;
  suggestedFormat?: DBIntervalFormat;
  suggestedIntervals?: number;
  progressionPath: DBIntervalProgressionPath;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  avgFatigueDrop: number;
  avgDistancePerInterval: number;
  avgRPE: number;
  consistencyTrend: 'improving' | 'stable' | 'declining';
  sessionsAnalyzed: number;
  readyForProgression: boolean;
  updatedAt: Date;
}

// ============================================================================
// DAILY ACTIVITY (NEAT / Wearable Data)
// ============================================================================

export interface DBDailyActivity {
  id: string;                         // `${userId}-${YYYY-MM-DD}`
  userId: string;
  date: Date;

  // Core movement
  steps: number;
  activeMinutes: number;
  activeCalories: number;
  distanceMeters: number;
  floorsClimbed: number;

  // Biometrics (optional)
  avgHeartRate?: number;
  restingHeartRate?: number;
  hrv?: number;
  bodyBatteryStart?: number;
  bodyBatteryEnd?: number;
  recoveryScore?: number;

  // Body composition (from Hume Pod via Google Fit)
  weight?: number;                      // kg
  bodyFatPercent?: number;              // 0-100
  spo2?: number;                        // Blood oxygen saturation %
  skinTemperature?: number;             // Celsius

  // Sleep
  sleepDurationMinutes?: number;
  sleepScore?: number;

  // HR zone breakdown for the day. Seconds in each of Z1-Z5 (50-60 /
  // 60-70 / 70-80 / 80-90 / 90+ %HRmax). Populated by Health Connect
  // sync when HeartRateSeries records are available. Used by the Z2
  // weekly accumulator (lib/zoneMinutes) as the preferred source over
  // per-session aggregation (wearable daily already includes workout
  // time when the user wears the watch during training).
  zoneMinutes?: { z1: number; z2: number; z3: number; z4: number; z5: number };

  // Calculated
  activityZone: string;               // ActivityZone as string for Dexie
  neatScore: number;                  // 0-100

  // Source & sync
  provider: string;                   // WearableProvider as string for Dexie
  rawPayload?: Record<string, unknown>;
  syncStatus: 'local' | 'synced' | 'pending';
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// ACTIVE WORKOUT (In-progress session backup for crash recovery)
// ============================================================================

export interface DBActiveWorkout {
  id: string;                         // `${userId}-${type}` (e.g., "abc123-pt")
  userId: string;
  type: 'pt' | 'assessment' | 'sprint' | 'freestyle' | 'pt-v2' | 'template';
  week?: number;
  day?: number;
  exercises: any[];                   // Full exercise state (WorkoutDisplay format)
  sessionNotes?: string;
  sessionStartTime: string;           // ISO timestamp
  expandedBlockIndex?: number;
  accumulatedTime?: number;           // Elapsed session seconds (avoids wall-clock drift)
  lastSaved: string;                  // ISO timestamp
}

// ============================================================================
// FREESTYLE TEMPLATE (User-saved workout templates)
// ============================================================================

export type FreestyleExerciseType = 'weight' | 'bodyweight' | 'cardio' | 'ruck' | 'carry' | 'timed';

export interface FreestyleTemplateExercise {
  name: string;
  type: FreestyleExerciseType;
  defaultSets: number;
  defaultWeight?: number;
  defaultReps?: number;
  defaultDistance?: number;
  defaultTime?: string;
}

export interface FreestyleTemplate {
  id: string;
  userId: string;
  name: string;
  exercises: FreestyleTemplateExercise[];
  createdAt: Date;
  lastUsedAt?: Date;
}

// ============================================================================
// DATABASE CLASS
// ============================================================================

class OPMODEDatabase extends Dexie {
  sessions!: Table<DBSession, string>;
  recovery!: Table<DBRecoveryEntry, string>;
  oneRMs!: Table<DB1RMEntry, string>;
  userProfiles!: Table<DBUserProfile, string>;
  progressionStates!: Table<DBProgressionState, string>;
  autoProgressDecisions!: Table<DBAutoProgressDecision, string>;
  intervalProgressDecisions!: Table<DBIntervalProgressDecision, string>;
  opmodeBridge!: Table<OpmodeBridgeRecord, string>;
  gunfighterBridge!: Table<GunfighterBridgeRecord, string>;
  assessmentSessions!: Table<DBAssessmentSession, string>;
  dailyActivity!: Table<DBDailyActivity, string>;
  activeWorkouts!: Table<DBActiveWorkout, string>;
  freestyleTemplates!: Table<FreestyleTemplate, string>;
  nutritionEntries!: Table<NutritionEntry, string>;
  customMeals!: Table<CustomMeal, string>;
  spearheadProgress!: Table<SpearheadProgress, string>;
  synthesisModifiers!: Table<any, string>;
  doubleProgressionState!: Table<any, string>;

  constructor() {
    super('opmode');
    
    // Version 1: Original schema with oderId typo
    this.version(1).stores({
      sessions: 'id, oderId, date, sessionType, programSelection, week, syncStatus, [oderId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, oderId, exerciseName, syncStatus, [oderId+exerciseName]',
      userProfiles: 'userId, syncedAt'
    });
    
    // Version 2: Normalize to userId (fix oderId typo)
    // Add userId index while keeping oderId for backwards compatibility
    this.version(2).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, syncedAt'
    }).upgrade(tx => {
      // Migrate sessions: copy oderId to userId
      return tx.table('sessions').toCollection().modify(session => {
        if (session.oderId && !session.userId) {
          session.userId = session.oderId;
        }
      }).then(() => {
        // Migrate 1RMs: copy oderId to userId
        return tx.table('oneRMs').toCollection().modify(oneRM => {
          if (oneRM.oderId && !oneRM.userId) {
            oneRM.userId = oneRM.oderId;
          }
        });
      });
    });
    
    // Version 3: Add updatedAt index to userProfiles for better querying
    this.version(3).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt'
    });
    
    // Version 4: Add progressionStates table for Intel/XP sync
    this.version(4).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt'
    });

    // Version 5: Add autoProgressDecisions table for workout weight progression
    this.version(5).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]'
    });

    // Version 6: Add intervalProgressDecisions table for HIIT/sprint training progression
    this.version(6).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]'
    });

    // Version 7: Add bridge tables for GunfighterOS <> OPMODE cross-app sync
    this.version(7).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt'
    });

    // Version 8: Add assessmentSessions table for multi-day assessment tracking
    this.version(8).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus'
    });

    // Version 9: Add dailyActivity table for NEAT / wearable tracking
    this.version(9).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]'
    });

    // Version 10: Add activeWorkouts table for crash-resistant session backup
    this.version(10).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved'
    });

    // Version 11: Add freestyleTemplates table for user-saved workout templates
    this.version(11).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved',
      freestyleTemplates: 'id, userId, name, createdAt'
    });

    // Version 12: Add nutritionEntries table for food/calorie tracking
    this.version(12).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved',
      freestyleTemplates: 'id, userId, name, createdAt',
      nutritionEntries: 'id, userId, date, mealType, syncStatus, [userId+date]'
    });

    // Version 13: Add customMeals table for saved recipes/meal combos
    this.version(13).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved',
      freestyleTemplates: 'id, userId, name, createdAt',
      nutritionEntries: 'id, userId, date, mealType, syncStatus, [userId+date]',
      customMeals: 'id, userId, name, syncStatus'
    });

    // Version 14: Add spearheadProgress table for Operation Spearhead territory war
    this.version(14).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved',
      freestyleTemplates: 'id, userId, name, createdAt',
      nutritionEntries: 'id, userId, date, mealType, syncStatus, [userId+date]',
      customMeals: 'id, userId, name, syncStatus',
      spearheadProgress: 'id, userId, operationId, syncStatus, updatedAt'
    });

    this.version(15).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved',
      freestyleTemplates: 'id, userId, name, createdAt',
      nutritionEntries: 'id, userId, date, mealType, syncStatus, [userId+date]',
      customMeals: 'id, userId, name, syncStatus',
      spearheadProgress: 'id, userId, operationId, syncStatus, updatedAt',
      synthesisModifiers: 'id'
    });

    this.version(16).stores({
      sessions: 'id, userId, oderId, date, sessionType, programSelection, week, syncStatus, [userId+date]',
      recovery: 'id, userId, date, syncStatus, [userId+date]',
      oneRMs: 'id, userId, oderId, exerciseName, syncStatus, [userId+exerciseName]',
      userProfiles: 'userId, updatedAt',
      progressionStates: 'userId, syncStatus, updatedAt',
      autoProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      intervalProgressDecisions: 'id, userId, exerciseName, updatedAt, [userId+exerciseName]',
      opmodeBridge: 'userId, snapshotAt',
      gunfighterBridge: 'userId, snapshotAt',
      assessmentSessions: '++id, odexieId, programId, weekNumber, completedAt, syncStatus',
      dailyActivity: 'id, userId, date, syncStatus, [userId+date]',
      activeWorkouts: 'id, userId, type, lastSaved',
      freestyleTemplates: 'id, userId, name, createdAt',
      nutritionEntries: 'id, userId, date, mealType, syncStatus, [userId+date]',
      customMeals: 'id, userId, name, syncStatus',
      spearheadProgress: 'id, userId, operationId, syncStatus, updatedAt',
      synthesisModifiers: 'id',
      doubleProgressionState: 'oderId, userId, [userId+oderId]'
    });
  }
}

// Singleton instance
export const db = new OPMODEDatabase();

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

/**
 * Check if migration from localStorage is needed
 * 
 * Returns true if localStorage has sessions that might not be in Dexie.
 * This handles the case where Dexie has some sessions but localStorage has newer ones.
 */
export function needsMigration(): boolean {
  // Skip if already marked as complete
  if (storage.get('dexie_migration_complete') === 'true') {
    return false;
  }

  // Check if localStorage has any data to migrate
  const localSessions = storage.get('sessionHistory');
  const localRecovery = storage.get('recoveryHistory');
  
  return !!(localSessions || localRecovery);
}

/**
 * Migrate all data from localStorage to Dexie
 */
export async function migrateFromLocalStorage(userId: string): Promise<{
  sessions: number;
  recovery: number;
  oneRMs: number;
}> {
  const stats = { sessions: 0, recovery: 0, oneRMs: 0 };
  
  console.log('� Starting migration from localStorage to Dexie...');
  
  // Migrate sessions
  const localSessions = storage.get('sessionHistory');
  if (localSessions) {
    try {
      const sessions = JSON.parse(localSessions);
      const dbSessions: DBSession[] = [];
      const seen = new Set<string>();
      
      for (const session of sessions) {
        // Skip duplicates BY ID ONLY - content-based deduplication causes data loss
        if (session.id && seen.has(session.id)) {
          console.warn(` Skipping duplicate session ID during Dexie migration: ${session.id}`);
          continue;
        }
        if (session.id) {
          seen.add(session.id);
        }
        
        dbSessions.push({
          ...session,
          userId: session.userId || userId,   //  Primary field (normalized)
          oderId: session.userId || userId,   // Backwards compat
          date: new Date(session.date),
          startTime: new Date(session.startTime || session.date),
          endTime: session.endTime ? new Date(session.endTime) : undefined,
          syncStatus: 'local',
          createdAt: new Date(session.date),
          updatedAt: new Date(),
        });
      }
      
      await db.sessions.bulkPut(dbSessions);
      stats.sessions = dbSessions.length;
      console.log(` Migrated ${stats.sessions} sessions`);
    } catch (error) {
      console.error(' Failed to migrate sessions:', error);
    }
  }
  
  // Migrate recovery
  const localRecovery = storage.get('recoveryHistory');
  if (localRecovery) {
    try {
      const recovery = JSON.parse(localRecovery);
      const dbRecovery: DBRecoveryEntry[] = recovery.map((entry: any) => ({
        id: entry.id || new Date(entry.date).toISOString().split('T')[0],
        userId: entry.userId || userId,
        date: new Date(entry.date),
        sleepHours: entry.sleepHours || 0,
        sleepQuality: entry.sleepQuality || 5,
        soreness: entry.soreness || 5,
        stress: entry.stress || 5,
        energy: entry.energy || 5,
        motivation: entry.motivation || 5,
        readinessScore: entry.readinessScore || entry.score || 70,
        notes: entry.notes,
        syncStatus: 'local' as const,
        createdAt: new Date(entry.date),
        updatedAt: new Date(),
      }));
      
      await db.recovery.bulkPut(dbRecovery);
      stats.recovery = dbRecovery.length;
      console.log(` Migrated ${stats.recovery} recovery entries`);
    } catch (error) {
      console.error(' Failed to migrate recovery:', error);
    }
  }
  
  // Migrate 1RMs
  const local1RMs = storage.get('estimated1RMs');
  if (local1RMs) {
    try {
      const oneRMs = JSON.parse(local1RMs);
      const dbOneRMs: DB1RMEntry[] = Object.entries(oneRMs).map(([exercise, weight]) => ({
        id: `${userId}-${exercise}`,
        userId: userId,   //  Primary field (normalized)
        oderId: userId,   // Backwards compat
        exerciseName: exercise,
        weight: weight as number,
        date: new Date(),
        source: 'calculated' as const,
        confidence: 0.8,
        syncStatus: 'local' as const,
        updatedAt: new Date(),
      }));
      
      await db.oneRMs.bulkPut(dbOneRMs);
      stats.oneRMs = dbOneRMs.length;
      console.log(` Migrated ${stats.oneRMs} 1RMs`);
    } catch (error) {
      console.error(' Failed to migrate 1RMs:', error);
    }
  }
  
  console.log(' Migration complete:', stats);
  return stats;
}

// ============================================================================
// SESSION API (Dexie-backed)
// ============================================================================

/**
 * Get all sessions for a user (replaces getSessionHistory)
 */
export async function getSessions(userId: string, options?: {
  limit?: number;
  programSelection?: string;
  since?: Date;
}): Promise<DBSession[]> {
  // Get all sessions for this user
  let sessions = await db.sessions.where('userId').equals(userId).toArray();
  
  // Filter by program if specified
  if (options?.programSelection) {
    sessions = sessions.filter(s => s.programSelection === options.programSelection);
  }
  
  // Filter by date if specified
  if (options?.since) {
    sessions = sessions.filter(s => new Date(s.date) >= options.since!);
  }
  
  // Sort by date ascending (oldest first) using actual Date comparison
  // Many functions use .slice(-N) to get recent sessions, expecting oldest-first order
  sessions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateA - dateB; // Ascending (oldest first)
  });
  
  // Apply limit
  if (options?.limit) {
    sessions = sessions.slice(0, options.limit);
  }
  
  return sessions;
}

/**
 * Get sessions for the current week (Mon-Sun)
 */
export async function getThisWeekSessions(userId: string): Promise<DBSession[]> {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - daysFromMonday);
  weekStart.setHours(0, 0, 0, 0);

  return db.sessions
    .where('[userId+date]')
    .between([userId, weekStart], [userId, today], true, true)
    .toArray();
}

/**
 * Save a session (replaces saveSession)
 */
/**
 * Compute and stamp `totalVolume` on a session.
 *
 * Extracted from saveSession because the Dexie write and the Firestore write
 * operate on DIFFERENT objects: saveSession mutated its own copy, while
 * lib/sessionStorage.ts handed a separately-built object to
 * saveSessionToFirebase which never had the field computed. Result — 218 of
 * 229 production sessions reached Firestore with no volume at all, so any
 * consumer reading it from the cloud copy (or from a device that restored
 * from cloud) saw zero.
 *
 * Mutates and returns the session so both callers can share one definition.
 */
export function stampTotalVolume<T extends DBSession>(session: T): T {
  // Calculate total volume for PT / Assessment / Sprint sessions
  // NOTE: `actualDistance` is stored in meters.
  if ((session.sessionType === 'pt' || session.sessionType === 'assessment' || session.sessionType === 'sprint') && session.exercises) {
    session.totalVolume = session.exercises.reduce((total, ex) => {
      let exerciseVolume = 0;
      
      // Traditional weight training volume
      if (ex.sets) {
        // Inline tonnage (per-hand/unilateral aware) — matches computeSetVolume
        // semantics. Inlined instead of importing volumeTracking to avoid a
        // circular import (volumeTracking → sessionStorage → … → db).
        exerciseVolume += ex.sets.reduce((sum, set) => {
          const s = set as any;
          const reps = s.reps || 0;
          const perHandMult = s.unilateral
            ? 1
            : ((typeof s.perHand === 'boolean' ? s.perHand : isPerHandLoad(ex.exerciseName)) ? 2 : 1);
          const totalReps = s.unilateral
            ? reps + (typeof s.rightReps === 'number' ? s.rightReps : reps)
            : reps;
          return sum + (s.weight || 0) * perHandMult * totalReps;
        }, 0);
      }
      
      // Loaded carries and rucks (distance-based volume)
      // Same normalization as `lib/volumeTracking.ts`: (totalWeight lbs × distance meters) / 10
      if ((ex.exerciseType === 'ruck' || ex.exerciseType === 'carry' || ex.exerciseType === 'cardio') &&
          ex.actualLoad && ex.actualDistance) {
        const plateCarrierWeight = ex.plateCarrierWeight || 0;
        const totalWeight = ex.actualLoad + plateCarrierWeight;
        exerciseVolume += (totalWeight * ex.actualDistance) / 10;
      }
      
      return total + exerciseVolume;
    }, 0);
  }
  // Range sessions get fixed volume
  else if (session.sessionType === 'range') {
    session.totalVolume = 2000;
  }
  return session;
}

export async function saveSession(session: DBSession): Promise<string> {
  session.updatedAt = new Date();
  session.createdAt = session.createdAt || new Date();
  session.syncStatus = session.syncStatus || 'local';
  
  stampTotalVolume(session);
  
  await db.sessions.put(session);
  return session.id;
}

/**
 * Delete a session
 */
export async function deleteSession(sessionId: string): Promise<void> {
  await db.sessions.delete(sessionId);
}

// ============================================================================
// RECOVERY API (Dexie-backed)
// ============================================================================

/**
 * Get recovery history
 */
export async function getRecoveryEntries(userId: string, options?: {
  limit?: number;
  since?: Date;
}): Promise<DBRecoveryEntry[]> {
  let entries = await db.recovery
    .where('userId').equals(userId)
    .reverse()
    .sortBy('date');
  
  if (options?.since) {
    entries = entries.filter(e => new Date(e.date) >= options.since!);
  }
  
  if (options?.limit) {
    entries = entries.slice(0, options.limit);
  }
  
  return entries;
}

/**
 * Get today's recovery entry
 */
export async function getTodayRecovery(userId: string): Promise<DBRecoveryEntry | undefined> {
  const today = getLocalDateString();
  // Try the correct ID format first (userId-date)
  let entry = await db.recovery.get(`${userId}-${today}`);

  // Fallback to legacy format (date only) for backwards compatibility
  if (!entry) {
    entry = await db.recovery.get(today);
  }

  // Final fallback: query by userId and date
  if (!entry) {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    entry = await db.recovery
      .where('userId').equals(userId)
      .and(r => {
        const d = new Date(r.date);
        return d >= todayStart && d <= todayEnd;
      })
      .first();
  }

  return entry;
}

/**
 * Save a recovery entry
 */
export async function saveRecoveryEntry(entry: DBRecoveryEntry): Promise<string> {
  entry.updatedAt = new Date();
  entry.createdAt = entry.createdAt || new Date();
  entry.syncStatus = entry.syncStatus || 'local';
  
  await db.recovery.put(entry);
  return entry.id;
}

// ============================================================================
// 1RM API (Dexie-backed)
// ============================================================================

/**
 * Get all 1RMs for a user
 */
export async function get1RMs(userId: string): Promise<Record<string, number>> {
  const entries = await db.oneRMs
    .where('userId').equals(userId)
    .toArray();
  
  return entries.reduce((acc, entry) => {
    acc[entry.exerciseName] = entry.weight;
    return acc;
  }, {} as Record<string, number>);
}

/**
 * Save a 1RM.
 *
 * The Dexie row ID is keyed on a NORMALIZED exercise name (lowercase,
 * no spaces / hyphens / underscores) so that "Back Squat", "back-squat",
 * and "backSquat" all map to the same record. Historically this didn't
 * exist and users accumulated duplicate rows (one per casing variant)
 * across legacy localStorage migrations, seed scripts, and assessment
 * writes. The displayed `exerciseName` field keeps the human-readable
 * form so the UI doesn't lose Title Case formatting.
 */
function normalizeOneRMKey(name: string): string {
  return name.toLowerCase().replace(/[\s\-_()]+/g, '');
}

/**
 * Persist the training baseline the load engine actually reads.
 *
 * Onboarding writes the profile to Firestore; the generator reads Dexie. Nothing
 * bridged the two for these fields, so bodyWeight/experienceLevel were invisible
 * to the load math. Call this anywhere the profile changes (onboarding, Settings,
 * a smart-scale weight update) so the engine sees it.
 *
 * Undefined fields are dropped rather than written, so a partial update never
 * clobbers a known value with a blank.
 */
export async function saveTrainingBaseline(
  userId: string,
  basics: Partial<Pick<DBUserProfile, 'displayName' | 'bodyWeight' | 'age' | 'gender' | 'experienceLevel'>>,
): Promise<void> {
  const patch: Record<string, any> = { updatedAt: new Date() };
  for (const [k, v] of Object.entries(basics)) {
    if (v !== undefined && v !== null) patch[k] = v;
  }

  const updated = await db.userProfiles.update(userId, patch);
  if (!updated) {
    await db.userProfiles.put({ userId, ...patch } as DBUserProfile);
  }
}

export async function save1RM(userId: string, exerciseName: string, weight: number, source: 'calculated' | 'tested' | 'manual' = 'calculated'): Promise<void> {
  const normalizedKey = normalizeOneRMKey(exerciseName);
  const id = `${userId}-${normalizedKey}`;

  await db.oneRMs.put({
    id,
    userId: userId,   //  Primary field (normalized)
    oderId: userId,   // Backwards compat
    exerciseName,     // Keep the human-readable display name
    weight,
    date: new Date(),
    source,
    confidence: source === 'tested' ? 1.0 : 0.8,
    syncStatus: 'local',
    updatedAt: new Date(),
  });

  //  BREACHER ARCHETYPE: Update 1RM total when squat/bench/deadlift changes
  const exNameLower = exerciseName.toLowerCase();
  if (exNameLower.includes('squat') || exNameLower.includes('bench') || exNameLower.includes('deadlift')) {
    try {
      const { updateBreacherOneRMTotal } = await import('./archetypeSystem');
      await updateBreacherOneRMTotal(userId);
      console.log('� Updated Breacher 1RM total after saving:', exerciseName);
    } catch (error) {
      console.warn(' Failed to update Breacher 1RM total (non-critical):', error);
    }
  }
}

// ============================================================================
// SYNC HELPERS
// ============================================================================

/**
 * Get all pending sync items
 */
export async function getPendingSyncItems(): Promise<{
  sessions: DBSession[];
  recovery: DBRecoveryEntry[];
  oneRMs: DB1RMEntry[];
  dailyActivity: DBDailyActivity[];
}> {
  const [sessions, recovery, oneRMs, dailyActivity] = await Promise.all([
    db.sessions.where('syncStatus').equals('local').toArray(),
    db.recovery.where('syncStatus').equals('local').toArray(),
    db.oneRMs.where('syncStatus').equals('local').toArray(),
    db.dailyActivity.where('syncStatus').equals('local').toArray(),
  ]);

  return { sessions, recovery, oneRMs, dailyActivity };
}

/**
 * Mark items as synced
 */
export async function markSynced(type: 'sessions' | 'recovery' | 'oneRMs' | 'progressionStates' | 'dailyActivity', ids: string[]): Promise<void> {
  const table = db[type];
  const now = new Date();
  
  await db.transaction('rw', table, async () => {
    for (const id of ids) {
      await table.update(id, { syncStatus: 'synced', syncedAt: now });
    }
  });
}

// ============================================================================
// ANALYTICS HELPERS
// ============================================================================

/**
 * Get volume for a date range
 */
export async function getVolumeInRange(userId: string, startDate: Date, endDate: Date): Promise<number> {
  const sessions = await db.sessions
    .where('[userId+date]')
    .between([userId, startDate], [userId, endDate], true, true)
    .filter(s => s.sessionType === 'pt' || s.sessionType === 'assessment')
    .toArray();

  return sessions.reduce((total, s) => total + (s.totalVolume || 0), 0);
}

/**
 * Get session count for a date range
 */
export async function getSessionCountInRange(userId: string, startDate: Date, endDate: Date): Promise<number> {
  return db.sessions
    .where('[userId+date]')
    .between([userId, startDate], [userId, endDate], true, true)
    .count();
}

// ============================================================================
// DAILY ACTIVITY API (Dexie-backed)
// ============================================================================

/**
 * Get daily activity entries for a user within a date range
 */
export async function getDailyActivityEntries(userId: string, options?: {
  since?: Date;
  until?: Date;
  limit?: number;
}): Promise<DBDailyActivity[]> {
  let entries = await db.dailyActivity
    .where('userId').equals(userId)
    .toArray();

  if (options?.since) {
    entries = entries.filter(e => new Date(e.date) >= options.since!);
  }
  if (options?.until) {
    entries = entries.filter(e => new Date(e.date) <= options.until!);
  }

  // Sort by date ascending
  entries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (options?.limit) {
    entries = entries.slice(-options.limit);
  }

  return entries;
}

/**
 * Get a day's activity entry. Defaults to today.
 *
 * The `dateStr` argument exists because the challenge engine evaluates
 * PAST days (midnight rollover, catch-up). Without it, evaluating
 * yesterday read today's steps/sleep/weight and either falsely credited
 * or falsely denied the commitment. Entry ids are `${userId}-${YYYY-MM-DD}`
 * built from the LOCAL date (see healthData.ts), so a keyed get is exact.
 */
export async function getTodayActivity(
  userId: string,
  dateStr: string = getLocalDateString(),
): Promise<DBDailyActivity | undefined> {
  return db.dailyActivity.get(`${userId}-${dateStr}`);
}

/**
 * Save a daily activity entry
 */
export async function saveDailyActivity(entry: DBDailyActivity): Promise<string> {
  entry.updatedAt = new Date();
  entry.createdAt = entry.createdAt || new Date();
  entry.syncStatus = entry.syncStatus || 'local';

  await db.dailyActivity.put(entry);
  return entry.id;
}

// ============================================================================
// BIOMETRIC PREFERENCES (stored in userProfiles.preferences)
// ============================================================================

export interface BiometricPreferences {
  heightInches?: number;
  sex?: 'male' | 'female';
}

export async function getBiometricPreferences(userId: string): Promise<BiometricPreferences> {
  const profile = await db.userProfiles.get(userId);
  return {
    heightInches: profile?.preferences?.heightInches ?? undefined,
    sex: profile?.preferences?.sex ?? undefined,
  };
}

export async function setBiometricPreferences(userId: string, prefs: Partial<BiometricPreferences>): Promise<void> {
  const existing = await db.userProfiles.get(userId);
  const merged = { ...(existing?.preferences ?? {}), ...prefs };
  if (existing) {
    await db.userProfiles.update(userId, { preferences: merged, updatedAt: new Date() });
  } else {
    await db.userProfiles.put({ userId, preferences: merged, updatedAt: new Date() });
  }
}

/**
 * Get activity entries within a date range
 */
export async function getActivityInRange(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<DBDailyActivity[]> {
  return getDailyActivityEntries(userId, { since: startDate, until: endDate });
}

// ============================================================================
// ACTIVE WORKOUT API (Crash-resistant session backup)
// ============================================================================

/**
 * Save or update the active workout state (Tier 1 - Dexie/IndexedDB)
 * Sub-millisecond writes, survives tab close and browser restart.
 */
export async function saveActiveWorkout(data: DBActiveWorkout): Promise<void> {
  data.lastSaved = new Date().toISOString();
  await db.activeWorkouts.put(data);
}

/**
 * Load the most recent active workout for a user, optionally matching type/week/day.
 */
export async function loadActiveWorkout(
  userId: string,
  type?: string,
  week?: number,
  day?: number
): Promise<DBActiveWorkout | undefined> {
  let entries = await db.activeWorkouts.where('userId').equals(userId).toArray();

  if (type) {
    entries = entries.filter(e => e.type === type);
  }
  if (week !== undefined) {
    entries = entries.filter(e => e.week === week);
  }
  if (day !== undefined) {
    entries = entries.filter(e => e.day === day);
  }

  if (entries.length === 0) return undefined;

  // Return the most recently saved
  entries.sort((a, b) => new Date(b.lastSaved).getTime() - new Date(a.lastSaved).getTime());
  return entries[0];
}

/**
 * Clear active workout after successful final save.
 */
export async function clearActiveWorkout(userId: string, type?: string): Promise<void> {
  if (type) {
    const id = `${userId}-${type}`;
    await db.activeWorkouts.delete(id);
  } else {
    // Clear all active workouts for user
    const entries = await db.activeWorkouts.where('userId').equals(userId).toArray();
    await db.activeWorkouts.bulkDelete(entries.map(e => e.id));
  }
}

// ============================================================================
// FREESTYLE TEMPLATES API
// ============================================================================

/**
 * Save a freestyle workout template
 */
export async function saveFreestyleTemplate(template: FreestyleTemplate): Promise<void> {
  template.createdAt = template.createdAt || new Date();
  await db.freestyleTemplates.put(template);
}

/**
 * Get all freestyle templates for a user
 */
export async function getFreestyleTemplates(userId: string): Promise<FreestyleTemplate[]> {
  return db.freestyleTemplates
    .where('userId').equals(userId)
    .reverse()
    .sortBy('createdAt');
}

/**
 * Get a single freestyle template by ID
 */
export async function getFreestyleTemplate(id: string): Promise<FreestyleTemplate | undefined> {
  return db.freestyleTemplates.get(id);
}

/**
 * Delete a freestyle template
 */
export async function deleteFreestyleTemplate(id: string): Promise<void> {
  await db.freestyleTemplates.delete(id);
}

/**
 * Update lastUsedAt timestamp on a template
 */
export async function touchFreestyleTemplate(id: string): Promise<void> {
  await db.freestyleTemplates.update(id, { lastUsedAt: new Date() });
}

// ============================================================================
// NUTRITION ENTRIES API (Dexie-backed)
// ============================================================================

/**
 * Save a nutrition entry
 */
export async function saveNutritionEntry(entry: NutritionEntry): Promise<void> {
  entry.updatedAt = new Date();
  entry.createdAt = entry.createdAt || new Date();
  entry.syncStatus = entry.syncStatus || 'local';
  await db.nutritionEntries.put(entry);
}

/**
 * Get all nutrition entries for a user on a specific date
 */
export async function getNutritionEntries(userId: string, date: string): Promise<NutritionEntry[]> {
  return db.nutritionEntries
    .where('[userId+date]')
    .equals([userId, date])
    .toArray();
}

/**
 * Delete a nutrition entry by ID
 */
export async function deleteNutritionEntry(id: string): Promise<void> {
  await db.nutritionEntries.delete(id);
}

/**
 * Update a nutrition entry
 */
export async function updateNutritionEntry(id: string, updates: Partial<NutritionEntry>): Promise<void> {
  await db.nutritionEntries.update(id, { ...updates, updatedAt: new Date(), syncStatus: 'local' });
}

/**
 * Get recent unique foods logged by a user (for "Recent Foods" list)
 */
export async function getRecentFoods(userId: string, limit = 20): Promise<NutritionEntry[]> {
  const entries = await db.nutritionEntries
    .where('userId').equals(userId)
    .reverse()
    .sortBy('createdAt');

  // Deduplicate by foodName + brandName
  const seen = new Set<string>();
  const unique: NutritionEntry[] = [];
  for (const entry of entries) {
    const key = `${entry.foodName}|${entry.brandName || ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(entry);
      if (unique.length >= limit) break;
    }
  }
  return unique;
}

// ============================================================================
// NUTRITION GOALS (stored in userProfiles.preferences)
// ============================================================================

export async function getNutritionGoals(userId: string): Promise<NutritionGoals> {
  const profile = await db.userProfiles.get(userId);
  return profile?.preferences?.nutritionGoals ?? DEFAULT_NUTRITION_GOALS;
}

export async function setNutritionGoals(userId: string, goals: NutritionGoals): Promise<void> {
  const existing = await db.userProfiles.get(userId);
  const merged = { ...(existing?.preferences ?? {}), nutritionGoals: goals };
  if (existing) {
    await db.userProfiles.update(userId, { preferences: merged, updatedAt: new Date() });
  } else {
    await db.userProfiles.put({ userId, preferences: merged, updatedAt: new Date() } as DBUserProfile);
  }
}

// ============================================================================
// CUSTOM MEALS API (Dexie-backed)
// ============================================================================

/**
 * Save a custom meal
 */
export async function saveCustomMeal(meal: CustomMeal): Promise<void> {
  meal.updatedAt = new Date();
  meal.createdAt = meal.createdAt || new Date();
  meal.syncStatus = meal.syncStatus || 'local';
  await db.customMeals.put(meal);
}

/**
 * Get all custom meals for a user
 */
export async function getCustomMeals(userId: string): Promise<CustomMeal[]> {
  return db.customMeals
    .where('userId').equals(userId)
    .toArray();
}

/**
 * Delete a custom meal by ID
 */
export async function deleteCustomMeal(id: string): Promise<void> {
  await db.customMeals.delete(id);
}

/**
 * Update a custom meal
 */
export async function updateCustomMeal(id: string, updates: Partial<CustomMeal>): Promise<void> {
  await db.customMeals.update(id, { ...updates, updatedAt: new Date(), syncStatus: 'local' });
}

export default db;

